import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { motion } from "framer-motion";
import { Platform, View } from "react-native";
import { Easing as EasingRA } from 'react-native-reanimated';
import { MotiView } from 'moti';
import { MouseState } from "./button";
import { TapGestureHandler } from "react-native-gesture-handler";
export const FadeTransitionContents = React.memo(function TransitionContents(props) {
    const transitionDuration = props.transitionDuration ?? 500;
    const [contents, setContents] = React.useState(props.children);
    const [isFadingOut, setIsFadingOut] = React.useState(false);
    React.useEffect(() => {
        if (props.children !== contents) {
            setIsFadingOut(true);
            setTimeout(() => {
                setContents(props.children);
                setIsFadingOut(false);
            }, transitionDuration);
        }
    }, [props.children, contents, transitionDuration]);
    return (_jsx(motion.div, { animate: {
            opacity: isFadingOut ? 0 : 1,
            transition: { duration: transitionDuration / 1000 }
        }, children: contents }));
});
const AnimateChangeInHeightNative = React.memo(function AnimateChangeInHeight(props) {
    const [height, setHeight] = React.useState(0);
    const onLayout = React.useCallback((e) => {
        setHeight(e.nativeEvent.layout.height);
    }, []);
    return (_jsx(MotiView, { animate: { height }, transition: {
            type: 'timing',
            easing: EasingRA.bezier(0.1, 0.12, 0.1, 0.95),
            duration: 150,
        }, children: _jsx(View, { onLayout: onLayout, style: { position: 'absolute', width: '100%' }, children: props.children }) }));
});
export const AnimateChangeInHeight = Platform.OS === "web" ? ({ children, transition, initalHeight, }) => {
    const containerRef = React.useRef(null);
    const [height, setHeight] = React.useState(initalHeight ?? "auto");
    React.useEffect(() => {
        if (containerRef.current) {
            const resizeObserver = new ResizeObserver((entries) => {
                // We only have one entry, so we can use entries[0].
                const observedHeight = entries[0].contentRect.height;
                setHeight(observedHeight);
            });
            resizeObserver.observe(containerRef.current);
            return () => {
                // Cleanup the observer when the component is unmounted
                resizeObserver.disconnect();
            };
        }
    }, []);
    return (_jsx(motion.div, { style: {
            height,
            overflow: "hidden",
        }, animate: { height }, transition: transition ?? { duration: 0.2, ease: [0.1, 0.12, 0.1, 0.95] }, children: _jsx("div", { ref: containerRef, children: children }) }));
} : AnimateChangeInHeightNative;
export const AnimateChangeInWidth = Platform.OS === "web" ? ({ children, transition, initialWidth, }) => {
    const containerRef = React.useRef(null);
    const [width, setWidth] = React.useState(initialWidth ?? "auto");
    React.useEffect(() => {
        if (containerRef.current) {
            const resizeObserver = new ResizeObserver((entries) => {
                // We only have one entry, so we can use entries[0].
                const observedWidth = entries[0].contentRect.width;
                setWidth(observedWidth);
            });
            resizeObserver.observe(containerRef.current);
            return () => {
                // Cleanup the observer when the component is unmounted
                resizeObserver.disconnect();
            };
        }
    }, []);
    return (_jsx(motion.div, { style: {
            width,
            overflow: "hidden",
        }, animate: { width }, transition: transition ?? { duration: 0.2, ease: [0.1, 0.12, 0.1, 0.95] }, children: _jsx("div", { ref: containerRef, children: children }) }));
} : AnimateChangeInHeightNative;
export function useMouseState() {
    const [mouseState, setMouseState] = React.useState(MouseState.none);
    const onMouseEnter = React.useCallback(() => {
        setMouseState(MouseState.hovered);
    }, []);
    const onMouseLeave = React.useCallback(() => {
        setMouseState(MouseState.none);
    }, []);
    const onMouseDown = React.useCallback(() => {
        setMouseState(MouseState.pressed);
    }, []);
    const onMouseUp = React.useCallback(() => {
        setMouseState(MouseState.none);
    }, []);
    return React.useMemo(() => ({
        mouseState,
        onMouseEnter,
        onMouseLeave,
        onMouseDown,
        onMouseUp,
    }), [mouseState, onMouseEnter, onMouseLeave, onMouseDown, onMouseUp]);
}
export const MouseInteraction = React.memo(function MouseInteraction(props) {
    const { onMouseEnter, onMouseLeave, onMouseDown, onMouseUp, mouseState } = useMouseState();
    const factor = React.useMemo(() => {
        return 0.025 * (props.factorShift ?? 1);
    }, []);
    if (Platform.OS === "web") {
        return (_jsx(motion.div, { animate: {
                scale: mouseState === MouseState.pressed ? (1 - factor) : mouseState === MouseState.hovered ? (1 + factor) : 1
            }, transition: {
                ease: [0.1, 0.12, 0.1, 0.95],
                duration: 0.1
            }, initial: { scale: 1 }, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onMouseDown: onMouseDown, onMouseUp: onMouseUp, style: props.fullSize === true ? {
                height: "100%",
                width: "100%"
            } : {}, children: props.children }));
    }
    return (_jsx(MotiView, { animate: {
            scale: mouseState === MouseState.pressed ? (1 - factor) : mouseState === MouseState.hovered ? (1 + factor) : 1
        }, transition: {
            easing: EasingRA.bezier(0.1, 0.12, 0.1, 0.95),
            duration: 0.1
        }, children: _jsx(TapGestureHandler, { onBegan: onMouseDown, onEnded: onMouseUp, children: _jsx(View, { style: { flex: 1 }, children: props.children }) }) }));
});
export const FadeInFromLeft = React.memo(function FadeInFromLeft(props) {
    return (_jsx(motion.div, { animate: {
            x: 0,
            opacity: 1
        }, initial: {
            x: -50,
            opacity: 0
        }, transition: {
            duration: props.duration ?? 0.5,
            ease: [0.1, 0.12, 0.1, 0.95]
        }, children: props.children }));
});
